<script setup lang="ts">
import { ref, onMounted } from 'vue'
import VersionContent from './VersionContent.vue'
import ColorContent from './ColorContent.vue'
import SummaryContent from './SummaryContent.vue'

defineOptions({
  name: 'ConfigurationTabs'
})

const activeTab = ref<'version' | 'color' | 'summary'>('version')

// État du mode de vue (extérieur/intérieur)
const viewMode = ref<'exterior' | 'interior'>('exterior')

// Écouter les changements de mode de vue
onMounted(() => {
  const handleViewModeChange = (event: Event) => {
    const customEvent = event as CustomEvent<{ mode: string }>
    const mode = customEvent.detail.mode
    if (mode === 'exterior' || mode === 'interior') {
      viewMode.value = mode
    }
  }
  
  window.addEventListener('viewModeChange' as any, handleViewModeChange)
})

// Tabs disponibles
const tabs = [
  { id: 'version', label: 'Version' },
  { id: 'color', label: 'Teintes carrosserie' },
  { id: 'summary', label: 'Synthèse' }
] as const

// Fonction pour changer de tab
function setActiveTab(tabId: 'version' | 'color' | 'summary') {
  activeTab.value = tabId
}

// Gestion du reset depuis SummaryContent
const handleReset = () => {
  activeTab.value = 'version'
}


</script>

<template>
  <div class="configuration-tabs">
    <!-- Tabs horizontaux -->
    <div class="tabs-header">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        :class="['tab-button', { active: activeTab === tab.id }]"
        @click="setActiveTab(tab.id)"
      >
        <!-- Icon Version -->
        <svg v-if="tab.id === 'version'" class="tab-icon" width="110" height="50" viewBox="0 0 110 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M68 19C59.6667 18.8333 42.2 18.5 39 18.5C35.8 18.5 30.3333 16.8333 28 16L31 12C32.8333 11.5 38.9 10.5 48.5 10.5C60.5 10.5 63 11.5 69.5 16.5" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M97 28.5H98V27L97 23.5C95 20.3 83.1666 19.1667 77.4999 19C69.8999 12.6 62.6666 10.3334 59.9999 10.0001C48.7999 8.00005 28.3333 10.8334 19.4999 12.5001V13.0001L17.9999 15.5001L16 17.0001C14.8 17.0001 13.8333 18.0001 13.4999 18.5001L13 20.0001C11.8 21.2001 12.1667 24.8334 12.5 26.5001L14.5 28.5001H16C19.5 24.0001 18.5 24.5 26.5 24C32.9 23.6 35.5 29.5 36 32.5H38.5C40.9 30.9 44.1667 30.5 45.5 30.5C51.5 30.3333 64.1 30.3 66.5 31.5C68.9 32.7 71.8333 33 73 33C74.9843 25.0629 75.9842 25.0005 83.8125 24.5117L84 24.5C90.4 24.1 93.3333 29 94 31.5C95.6 31.5 96.6667 29.5 97 28.5Z" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="25" cy="36" r="7" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="84" cy="36" r="7" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M32.0001 37H77.0001M12.5 26.5L11.5 26.9565C11.3334 27.7174 11.2 29.2391 12 29.2391L11.0001 30.1522C11 31.3696 11.3 33.8957 12.5 34.2609C13.7001 34.6261 16.6667 35.3261 18.0001 35.6304" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M68 19.5V17C68.8 15.8 70.6667 16.8333 71.5 17.5C71.9 19.1 69.3333 19.5 68 19.5Z" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M71.5 18C72.6667 18.6667 74.8 22.3 74 29.5" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M90.5 38.5C92.6667 38.6667 97.2 38.7 98 37.5L98.5 36.5L98 36L98.5 31L98 30.5V28.5" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M79 19.5C79.6667 20 81.3 21 82.5 21C84 21 88.5 21 90 22M90 22C91.5 23 96 27.5 96.5 27C97 26.5 97 24 97 23.5M90 22C91.6667 22.1667 95.3 22.6 96.5 23" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M14 18L15.5 18.5L17 21.5L16 23L13 22.5L12.5 21.5" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M48.5 10.5L49.5 18.5C49.8333 19.5 50.5 23.3 50.5 30.5V37" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M28 16C27 17.5 25.5 21.2 27.5 24" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M16 17H17L23.5 13M23.5 13L31 12M23.5 13L24.5 12" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M28 11C28.3333 10.6667 28.8 9.90002 28 9.50002C29.1667 9.33335 31.6 9.30002 32 10.5" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>

        <!-- Icon Teinte -->
        <svg v-if="tab.id === 'color'" class="tab-icon" width="110" height="50" viewBox="0 0 110 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M64.8441 7.0268H65.5791C65.8518 7.0268 66.073 6.80874 66.073 6.54006C66.073 6.27138 65.8518 6.05331 65.5791 6.05331H64.8441C64.5714 6.05331 64.3501 6.27138 64.3501 6.54006C64.3501 6.80874 64.5714 7.0268 64.8441 7.0268Z" fill="currentColor"/>
          <path d="M74.4402 6.05331H67.0807C66.8081 6.05331 66.5868 6.27138 66.5868 6.54006C66.5868 6.80874 66.8081 7.0268 67.0807 7.0268H74.0121V13.8594H62.1707V7.0268H63.3312C63.6039 7.0268 63.8252 6.80874 63.8252 6.54006C63.8252 6.27138 63.6039 6.05331 63.3312 6.05331H61.7426C61.4337 6.05331 61.1828 6.30058 61.1828 6.60496V37.1408C61.1828 40.8945 64.2823 43.948 68.0917 43.948C71.9012 43.948 75 40.8939 75 37.1408V6.60496C75 6.30058 74.7491 6.05331 74.4402 6.05331ZM74.0121 37.1408C74.0121 40.3578 71.3559 42.9746 68.0917 42.9746C64.8276 42.9746 62.1707 40.3572 62.1707 37.1408V30.4457H74.0121V37.1408ZM74.0121 29.4722H62.1707V22.6396H74.0121V29.4722ZM74.0121 21.6662H62.1707V14.8335H74.0121V21.6662Z" fill="currentColor"/>
          <path d="M35.494 40.8271C35.7666 40.8271 35.9879 40.609 35.9879 40.3403V39.6161C35.9879 39.3474 35.7666 39.1293 35.494 39.1293C35.2213 39.1293 35 39.3474 35 39.6161V40.3403C35 40.609 35.2213 40.8271 35.494 40.8271Z" fill="currentColor"/>
          <path d="M35.494 38.6237C35.7666 38.6237 35.9879 38.4056 35.9879 38.1369V31.3069H38.7963C39.069 31.3069 39.2903 31.0889 39.2903 30.8202C39.2903 30.5515 39.069 30.3334 38.7963 30.3334H35.5598C35.2509 30.3334 35 30.5807 35 30.8851V38.1369C35 38.4056 35.2213 38.6237 35.494 38.6237Z" fill="currentColor"/>
          <path d="M63.0223 42.9746H59.7542V42.3295C59.7542 42.0608 59.533 41.8427 59.2603 41.8427C58.9876 41.8427 58.7663 42.0608 58.7663 42.3295V42.9746H51.8324V38.0539C51.8324 37.7852 51.6111 37.5672 51.3384 37.5672C51.0657 37.5672 50.8444 37.7852 50.8444 38.0539V42.9746H43.9105V34.6973C43.9105 34.4286 43.6892 34.2106 43.4165 34.2106C43.1438 34.2106 42.9225 34.4286 42.9225 34.6973V42.9746H35.9879V41.8311C35.9879 41.5624 35.7666 41.3443 35.494 41.3443C35.2213 41.3443 35 41.5624 35 41.8311V43.3964C35 43.7008 35.2509 43.9481 35.5598 43.9481H63.0223C63.295 43.9481 63.5163 43.73 63.5163 43.4613C63.5163 43.1926 63.295 42.9746 63.0223 42.9746Z" fill="currentColor"/>
          <path d="M37.4652 24.7476C37.5429 24.7917 37.6279 24.8131 37.7115 24.8131C37.8821 24.8131 38.0481 24.7255 38.1396 24.5697L38.5071 23.9422C38.6435 23.7092 38.5625 23.4119 38.326 23.277C38.0896 23.1426 37.7879 23.2224 37.6509 23.4554L37.2834 24.083C37.1471 24.316 37.2281 24.6132 37.4645 24.7482L37.4652 24.7476Z" fill="currentColor"/>
          <path d="M35.9385 27.4831L43.1702 31.5971L50.0323 35.5014L56.9235 39.422L59.8115 41.0652C59.8892 41.1094 59.9742 41.1308 60.0578 41.1308C60.2284 41.1308 60.3944 41.0438 60.486 40.8874C60.6223 40.6544 60.5413 40.3572 60.3048 40.2222L57.3542 38.5439L58.1505 36.9227C58.269 36.6806 58.1663 36.3899 57.9213 36.2724C57.6756 36.1549 57.3806 36.2568 57.2613 36.4983L56.4967 38.0552L50.5223 34.6564L53.4295 29.6507C53.5652 29.4177 53.4828 29.1198 53.2464 28.9868C53.0093 28.8531 52.7076 28.9342 52.5726 29.1679L49.6675 34.1703L43.6641 30.7547L48.4062 22.6617C48.5425 22.4287 48.4615 22.1315 48.2251 21.9965C47.9886 21.8622 47.687 21.942 47.55 22.175L42.8079 30.2679L36.8026 26.8516L37.3829 25.8613C37.5192 25.6283 37.4382 25.331 37.2017 25.196C36.9653 25.0617 36.6636 25.1415 36.5267 25.3745L35.7324 26.7296C35.5782 26.9931 35.6698 27.3312 35.9372 27.4831H35.9385Z" fill="currentColor"/>
          <path d="M38.5836 22.8395C38.6613 22.8837 38.7462 22.9051 38.8299 22.9051C39.0005 22.9051 39.1664 22.8175 39.258 22.6617L42.7237 16.7468L44.9412 18.0084C45.1777 18.1428 45.4793 18.063 45.6163 17.83C45.7527 17.597 45.6716 17.2997 45.4352 17.1647L42.8475 15.6922C42.7177 15.6182 42.5669 15.5987 42.4227 15.637C42.2784 15.6753 42.1572 15.7662 42.0828 15.894L38.4031 22.1743C38.2668 22.4073 38.3478 22.7046 38.5842 22.8395H38.5836Z" fill="currentColor"/>
          <path d="M48.7514 11.7632C48.835 11.7632 48.92 11.7424 48.9977 11.6976L49.6346 11.3355C49.871 11.2011 49.952 10.9033 49.8157 10.6703C49.6794 10.4373 49.3771 10.3575 49.1406 10.4918L48.5037 10.8539C48.2673 10.9883 48.1863 11.2862 48.3226 11.5192C48.4141 11.6756 48.5801 11.7625 48.7507 11.7625L48.7514 11.7632Z" fill="currentColor"/>
          <path d="M49.2941 19.4193L53.7496 27.0236L57.216 32.9398L59.0305 36.0374C59.122 36.1938 59.288 36.2808 59.4586 36.2808C59.5422 36.2808 59.6272 36.26 59.7049 36.2152C59.9413 36.0809 60.0223 35.783 59.886 35.55L58.5655 33.2954L60.1646 32.3855C60.401 32.2512 60.4821 31.9533 60.3457 31.7203C60.2094 31.4873 59.9071 31.4082 59.6706 31.5418L58.0715 32.4517L54.6039 26.5336L59.508 23.7306C59.7437 23.5956 59.8241 23.2977 59.6871 23.0653C59.5501 22.833 59.2478 22.7538 59.012 22.8888L54.1099 25.6912L50.6436 19.7749L59.1839 14.9159C59.4204 14.7816 59.5014 14.4837 59.365 14.2507C59.2287 14.0177 58.9264 13.9379 58.6899 14.0722L50.1496 18.9313L46.6826 13.0137L47.6877 12.442C47.9241 12.3076 48.0051 12.0097 47.8688 11.7767C47.7325 11.5438 47.4302 11.4639 47.1937 11.5983L45.8179 12.381C45.5505 12.5335 45.4589 12.8709 45.613 13.1344L49.2941 19.4173V19.4193Z" fill="currentColor"/>
          <path d="M50.6883 10.6618C50.772 10.6618 50.8569 10.641 50.9346 10.5962L56.9472 7.1754L58.516 9.69219C58.6589 9.92129 58.9632 9.99268 59.1957 9.8525C59.4282 9.71166 59.5007 9.41183 59.3584 9.18273L57.5742 6.32002C57.4175 6.06302 57.0789 5.97606 56.8148 6.12662L50.4413 9.75255C50.2049 9.88689 50.1239 10.1848 50.2602 10.4178C50.3518 10.5742 50.5177 10.6611 50.6883 10.6611V10.6618Z" fill="currentColor"/>
          <path d="M68.0917 39.566C69.6902 39.566 70.9903 38.2849 70.9903 36.7098C70.9903 35.1347 69.6902 33.8542 68.0917 33.8542C66.4932 33.8542 65.1931 35.1354 65.1931 36.7098C65.1931 38.2843 66.4932 39.566 68.0917 39.566ZM68.0917 34.8277C69.1448 34.8277 70.0024 35.6721 70.0024 36.7098C70.0024 37.7476 69.1455 38.5926 68.0917 38.5926C67.0379 38.5926 66.181 37.7482 66.181 36.7098C66.181 35.6714 67.0379 34.8277 68.0917 34.8277Z" fill="currentColor"/>
        </svg>

        <!-- Icon Synthèse -->
        <svg v-if="tab.id === 'summary'" class="tab-icon" width="110" height="50" viewBox="0 0 110 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="37.5" y="3.5" width="32" height="43" stroke="currentColor" stroke-linejoin="round"/>
          <line x1="41.5" y1="11.5" x2="65.5" y2="11.5" stroke="currentColor" stroke-linecap="round"/>
          <line x1="41.5" y1="25.5" x2="65.5" y2="25.5" stroke="currentColor" stroke-linecap="round"/>
          <line x1="41.5" y1="39.5" x2="65.5" y2="39.5" stroke="currentColor" stroke-linecap="round"/>
          <line x1="41.5" y1="18.5" x2="65.5" y2="18.5" stroke="currentColor" stroke-linecap="round"/>
          <line x1="41.5" y1="32.5" x2="65.5" y2="32.5" stroke="currentColor" stroke-linecap="round"/>
        </svg>

        <span class="tab-label">{{ tab.label }}</span>
      </button>
    </div>

    <!-- Zone de contenu du tab -->
    <div class="tab-content">
      <!-- Contenu Version -->
      <VersionContent 
        v-if="activeTab === 'version'" 
        @nextStep="setActiveTab('color')"
      />

      <!-- Contenu Teinte -->
      <ColorContent 
        v-if="activeTab === 'color'" 
        @nextStep="setActiveTab('summary')"
      />

      <!-- Contenu Synthèse -->
      <SummaryContent v-if="activeTab === 'summary'" @reset="handleReset" />
    </div>
  </div>
</template>

<style scoped>
.configuration-tabs {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 875px;
  margin-inline: auto;
}

.tabs-header {
  display: flex;
  gap: 0.5rem;
}

.tab-button {
  position: relative;
  flex: 1;
  margin: 0;
  padding: 0.25em 0;
  color: var(--color-text-primary);
  border: none;
  box-shadow: none;
  font-size: 0.875rem;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  &::before {
    content: '';
    display: block;
    position: absolute;
    inset: 0;
    background-color: var(--tab-default);
    transform: scaleY(0.8);
    transition: background-color 0.3s ease, transform 0.3s ease;
    transform-origin: center;
  }
}

.tab-icon {
  margin-block-start: 1rem;
  opacity: 0.5;
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.tab-label {
  display: block;
  opacity: 0;
  transform: translateY(10px);
  transition: transform 0.3s ease;
}

.tab-button:hover,
.tab-button.active {
    &::before {
    background-color: var(--tab-active);
    transform: scaleY(1);
  }
}

.tab-button:hover .tab-icon,
.tab-button.active .tab-icon {
  opacity: 1;
  transform: translateY(-10px);
}

.tab-button:hover .tab-label,
.tab-button.active .tab-label {
  opacity: 1;
  transform: translateY(0);
}

.tab-content {
  background: var(--tab-active);
  padding: 16px;
  overflow-y: auto;
}

.configure-section {
  padding: 1rem 0 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.precommand-link {
  padding: 0.5em 3em;
  background-color: var(--color-text-primary);
  color: var(--color-primary);
  border: none;
  border-radius: 0.25rem;
  font-size: 1rem;
  font-weight: 300;;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;
  letter-spacing: 0.5px;
  text-decoration: none;
}

.precommand-link:hover {
  background-color: var(--color-hover);
  color: var(--color-primary);
}

.precommand-link:active {
  color: var(--color-primary);
}

.precommand-link.disabled {
  background-color: var(--color-disabled, #999);
  color: var(--color-disabled-text, #666);
  cursor: not-allowed;
  pointer-events: none;
}

.precommand-link.disabled:hover {
  background-color: var(--color-disabled, #999);
  color: var(--color-disabled-text, #666);
}

/* Responsive */
@media (max-width: 768px) {
  .tabs-header {
    padding: 0 16px;
  }

  .tab-button {
    padding: 12px 8px;
    font-size: 14px;
    gap: 6px;
  }

  .configure-section {
    padding: 20px 16px;
  }

  .precommand-link {
    padding: 16px 24px;
  }
}

@media (max-width: 480px) {
  .tabs-header {
    padding: 0 12px;
  }

  .tab-button {
    padding: 10px 6px;
    font-size: 12px;
    gap: 4px;
  }

  .tab-content {
    padding: 12px;
  }

  .configure-section {
    padding: 16px 12px;
  }

  .precommand-link {
    padding: 14px 20px;
  }
}
</style>